import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import {
  client,
  NewCommentFile,
  FormFilesDocumentTypes,
  TaskComment,
  TaskEvent,
  OutboundTaskType,
  OutboundTaskTypeKey,
} from '../../api';
import type { Task, WithAdditionalTaskResponseFields } from './types';
import { Sidebar, SidebarTopBar } from 'design-system/src/components/Sidebar/Sidebar';
import React, { useContext, useState, useEffect } from 'react';
import { DrugPanel, PatientPanel, PrescriberPanel } from '../sidebar/SidebarPanels';
import { TaskContext } from './contexts';
import { TimelineItemList, TimelineItem } from '../TimelineItem/TimelineItem';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import Badge from 'design-system/src/components/Badge/Badge';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { ItemViewTopNav } from '../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../tableItemDetailViewComponents/Containers';
import { GeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/GeneralInfoTable';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { useNavigate } from 'react-router-dom';
import DeclineModal from './DeclineModal';
import TaskActions from './TaskActions';
import AppealRequestModal, { AdditionalInput, AppealRequestModalType } from './AppealRequestModal';
import styled from 'styled-components';
import SubmitAppealModal from './SubmitAppealModal';
import NewFollowupTaskModal from './NewFollowupTaskModal';
import AppealEditModal from './AppealEditModal';
import BusinessRelationshipSmallPrint from '../businessRelationshipNotices/BusinessRelationshipSmallPrint/BusinessRelationshipSmallPrint';
import { getBenefitTypeLabel } from './utils';
import { sponsoredTaskCopy, sponsoredTaskCopyWithPaNotice } from '../businessRelationshipNotices/copy';

type ModalState = {
  show: boolean;
  type: 'decline' | 'initiate' | 'submit_appeal' | 'other_request' | 'edit_appeal';
};

export const InstructionsContainer = styled.div`
  background-color: var(--surface-200);
  padding: 12px;
  border-radius: var(--border-radius-medium);
  margin-bottom: 16px;
`;

const getBusinessRelationshipNoticeCopy = (taskKey: OutboundTaskTypeKey) => {
  switch (taskKey) {
    case OutboundTaskTypeKey.PA_RESUBMISSION:
    case OutboundTaskTypeKey.PA_EXPIRING:
      return sponsoredTaskCopyWithPaNotice;

    default:
      return sponsoredTaskCopy;
  }
};

/**
 * Component for rendering a single structured task.
 *
 * TODO: split these out into separate components for each task type
 */
const TaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Files that have been uploaded as part of composing a new comment
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [additionalInput, setAdditionalInput] = useState<AdditionalInput>({
    needs_draft_review: false,
  });
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    type: 'decline',
  });
  const [inputValue, setInputValue] = useState<string>('');
  const [taskResponseLoading, setTaskResponseLoading] = useState(false);
  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const [documentId, setDocumentId] = useState<string | null>(null);

  const initiate = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "initiate" for task ID: ${taskId}`);

        const newComment: TaskComment = {
          id: `mock-initiate-${Date.now()}`,
          item_type: 'comment',
          direction: 'inbound',
          message: inputValue,
          user: 'You',
          attachment_document_ids: newFilesUploaded.map((file) => file.file.name),
          created_at: new Date().toISOString(),
        };

        setMockComments((prev) => [...prev, newComment]);

        setNewFilesUploaded([]);
        setInputValue('');
        return;
      }

      const formData = new FormData();
      formData.append('task_response', 'initiate');
      formData.append('message', inputRef.current?.value || '');
      formData.append('additional_input', JSON.stringify(additionalInput));

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};
        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });
        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task initiated.</Toast>);
      navigate('/tasks');
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const [mockComments, setMockComments] = useState<TaskComment[]>([]);

  useEffect(() => {
    return () => {
      setMockComments([]); // Reset mock comments on unmount
    };
  }, []);

  const closeModal = () => {
    setModalState({ ...modalState, show: false });
  };

  useEffect(() => {
    const handleFormMessage = (messageEvent: MessageEvent) => {
      if (!messageEvent.data) return;

      try {
        if (!messageEvent.data.type) return;

        const event = messageEvent.data.type.split('embed.form.')[1];

        switch (event) {
          case 'completed':
            onTaskUpdate();
            toast.custom(() => <Toast variant="success">Form signed.</Toast>);
            navigate('/tasks');
            break;
          case 'exception':
            toast.custom(() => <Toast variant="error">Error, please try again.</Toast>);
            break;
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    };

    window.addEventListener('message', handleFormMessage);
    return () => window.removeEventListener('message', handleFormMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // We're managing the document ID in the state so that it can be updated easily on edits in AppealEditModal
  const taskDocumentId = taskResponse.task.document_id;
  useEffect(() => {
    if (taskDocumentId) {
      setDocumentId(taskDocumentId);
    }
  }, [taskDocumentId]);

  let modalToShow;
  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const showSidebar = drug && patient && prescriber;
  const extraData: { label: string; value: string }[] = [];
  const benefitTypeLabel = getBenefitTypeLabel(task);
  if (benefitTypeLabel) {
    extraData.push({ label: 'Benefit type', value: benefitTypeLabel });
  }

  const declineModal = (
    <DeclineModal
      closeModal={closeModal}
      patient={patient}
      drug={drug}
      // the DeclineModal expects an OutboundTaskTypeKey because it's only used for outbound tasks.
      // here, the task_key could also be an InboundTaskTypeKey, so we need to explicitly cast.
      taskKey={task.task_key as OutboundTaskTypeKey}
      taskId={taskId}
      onTaskUpdate={onTaskUpdate}
    />
  );

  let appealRequestModalType: AppealRequestModalType | undefined;
  if (task.task_key === OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS) {
    appealRequestModalType = 'initiate_with_next_steps';
  }

  const initiateModal = appealRequestModalType && (
    <AppealRequestModal
      closeModal={closeModal}
      initiate={initiate}
      inputRef={inputRef}
      patient={patient}
      drug={drug}
      task={task}
      inputValue={inputValue}
      setInputValue={setInputValue}
      files={newFilesUploaded}
      onFilesChange={setNewFilesUploaded}
      setAdditionalInput={setAdditionalInput}
      additionalInput={additionalInput}
      appealRequestModalType={appealRequestModalType as AppealRequestModalType}
    />
  );

  const submitAppealModal = (
    <SubmitAppealModal
      closeModal={closeModal}
      patient={patient}
      drug={drug}
      taskId={taskId}
      includeEnrollmentNotice={false}
    />
  );

  const newFollowupTaskModal = (
    <NewFollowupTaskModal
      closeModal={closeModal}
      patient={patient}
      drug={drug}
      patientId={patient.id}
      prescriptionId={drug.id}
      taskId={taskId}
      relatedTaskKey={task.task_key as OutboundTaskTypeKey}
      relatedTaskType={task.type as OutboundTaskType}
    />
  );

  const appealEditModal = documentId && (
    <AppealEditModal
      closeModal={closeModal}
      patient={patient}
      drug={drug}
      documentId={documentId}
      setDocumentId={setDocumentId}
      taskId={taskId}
    />
  );

  switch (modalState.type) {
    case 'decline':
      modalToShow = declineModal;
      break;
    case 'initiate':
      modalToShow = initiateModal;
      break;
    case 'submit_appeal':
      modalToShow = submitAppealModal;
      break;
    case 'other_request':
      modalToShow = newFollowupTaskModal;
      break;
    case 'edit_appeal':
      modalToShow = appealEditModal;
      break;
  }

  const showBusinessRelationshipNotice = task.is_pharma_contract_task;
  const businessRelationshipNoticeCopy = getBusinessRelationshipNoticeCopy(task.task_key as OutboundTaskTypeKey);

  // Additional check that task.timeline isn't null so that this doesn't result in an error when executed
  const comments = (
    <TimelineItemList>
      <>
        {task.timeline.map((item, index) => {
          let author;
          let timestamp;
          let message;
          let attachedFilesIds;
          let lastEditedAt;
          if (item.item_type === 'comment') {
            const comment = item as TaskComment;
            timestamp = comment.created_at;
            author = comment.direction === 'inbound' ? comment.user : 'Tandem';
            message = comment.message;
            attachedFilesIds = comment.attachment_document_ids;
            lastEditedAt = comment.last_edited_at;
          } else {
            const event = item as TaskEvent;
            timestamp = event.event_timestamp;
            author = event.user;
            message = event.label;
            attachedFilesIds = event.attachment_document_ids;
          }
          return (
            <TimelineItem
              key={`task-item-${item.id}`}
              isLastEventInList={index === task.timeline.length - 1}
              author={author}
              timestamp={timestamp}
              message={message}
              attachedFilesIds={attachedFilesIds}
              lastEditedAt={lastEditedAt}
            />
          );
        })}

        {mockComments.map((mockComment, index) => (
          <TimelineItem
            key={`mock-${mockComment.id}`}
            isLastEventInList={index === mockComments.length - 1}
            author={mockComment.user}
            timestamp={mockComment.created_at}
            message={mockComment.message}
            attachedFilesIds={mockComment.attachment_document_ids}
            lastEditedAt={mockComment.last_edited_at}
          />
        ))}
      </>
    </TimelineItemList>
  );

  const errors = [];
  const hasForm = !!task.form;
  if (hasForm && newFilesUploaded.length > 0) {
    errors.push(
      'Note: Tandem can only manage enrollments with Tandem-initiated forms. Please do not upload forms initiated by your office.',
    );
  }

  const body = (
    <BodyContainer>
      <TopNavAndContent>
        <ItemViewTopNav
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          noSidebar={!showSidebar}
          additionalRightSideContent={task.status === 'completed' && <Badge variant="neutral">Closed</Badge>}
        />

        <ContentOuterWrapper>
          <CenteredContent>
            <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
              {task.task_title || task.type}
            </Typography>

            <GeneralInfoTable
              patientName={drug.patient_name}
              prescriberName={drug.prescriber_name}
              dob={drug.dob}
              prescribedOn={drug.prescription_recieved_at}
              drugName={drug.name}
              prescriptionId={drug.id}
              extraData={extraData}
            />

            {task.timeline && comments}

            {task.timeline && <Hairline />}

            {(task.status !== 'completed' ||
              task.task_key === OutboundTaskTypeKey.BENEFITS_ROUTING_CONFIRMATION ||
              task.task_key === OutboundTaskTypeKey.BENEFITS_ROUTING_SELECTION) && (
              <>
                {taskResponseLoading ? (
                  <LoadingSpinner height="200px" />
                ) : (
                  <TaskActions
                    setShowDeclineModal={() => setModalState({ show: true, type: 'decline' })}
                    task={task}
                    setTaskResponseLoading={setTaskResponseLoading}
                    setShowNewFollowupTaskModal={() => setModalState({ show: true, type: 'other_request' })}
                    rxId={drug.id}
                    drugName={drug.name}
                    drugNdc={drug.ndc}
                  />
                )}
              </>
            )}

            <BusinessRelationshipSmallPrint />
          </CenteredContent>
        </ContentOuterWrapper>
      </TopNavAndContent>

      {showSidebar && !isSidebarCollapsed && (
        <Sidebar>
          <SidebarTopBar />
          <DrugPanel
            drug={drug}
            showBusinessRelationshipNotice={showBusinessRelationshipNotice}
            businessRelationshipNoticeCopy={businessRelationshipNoticeCopy}
          />
          <PatientPanel patient={patient} />
          <PrescriberPanel prescriber={prescriber} />
        </Sidebar>
      )}
    </BodyContainer>
  );

  return (
    <>
      {body}

      {modalState.show && modalToShow}
    </>
  );
};

export default TaskView;
